import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { useRoles } from '@/features/admin';
import type { PolicyRule, PolicyVocabulary } from '@/shared/api/generated/policy';

import { documentFrom, isComplete } from '../model/builder';
import type { DraftRule } from '../model/builder';
import type { EnforcementReading } from '../model/bundles';
import {
  activeStage,
  checkAccess,
  numbered,
  outcomeOf,
  roleGrants,
} from '../model/workspace';
import type {
  AccessCheck,
  NumberedRule,
  PolicyStage,
  StepOutcome,
} from '../model/workspace';
import { resourceLabel, resourceName, resourceOne, ruleSentence } from './policyText';

/* "Can this role do this?", answered the way the platform answers it.
 *
 * Roles first, from what the tenant's roles actually store — a role that does not hold the
 * permission is refused whatever the rules say — then the attribute policy, as the tenant's real
 * enforcement state puts it: skipped when policy is off or has nothing to apply, refusing
 * everything when the governing bundle has no usable version, and otherwise the rules, where deny
 * wins and a request no rule allows is refused.
 */

export interface CheckAccessPanelProps {
  readonly vocabulary: PolicyVocabulary | undefined;
  readonly datasetNames: ReadonlyMap<string, string> | undefined;
  readonly reading: EnforcementReading;
  readonly activeSemver: string | null;
  /** `undefined` while read, `null` when uploaded as compiled policy. */
  readonly activeRules: readonly PolicyRule[] | null | undefined;
  readonly draftRules: readonly PolicyRule[] | null | undefined;
  readonly hasDraft: boolean;
  /** The editor's rows when they differ from what was saved, otherwise `null`. */
  readonly unsaved: readonly DraftRule[] | null;
  readonly policyOff: boolean;
}

interface Request {
  readonly role: string;
  readonly resourceType: string;
  readonly action: string;
  readonly resourceId: string;
}

const MARK = { pass: '✓', fail: '✕', skip: '–', unknown: '?' } as const satisfies Record<
  StepOutcome,
  string
>;

export function CheckAccessPanel({
  vocabulary,
  datasetNames,
  reading,
  activeSemver,
  activeRules,
  draftRules,
  hasDraft,
  unsaved,
  policyOff,
}: CheckAccessPanelProps): ReactElement {
  const { t } = useTranslation();
  const roles = useRoles();
  const [against, setAgainst] = useState<'active' | 'draft'>('active');
  const [chosen, setChosen] = useState<Request | null>(null);

  const firstType =
    vocabulary?.resources.find((each) => each.resource_type === 'catalog.dataset') ??
    vocabulary?.resources[0];
  const request: Request = chosen ?? {
    role: vocabulary?.roles[0] ?? '',
    resourceType: firstType?.resource_type ?? '',
    action: firstType?.actions[0] ?? '',
    resourceId: '',
  };
  const actions =
    vocabulary?.resources.find((each) => each.resource_type === request.resourceType)
      ?.actions ?? [];
  const one = request.resourceType === '' ? '' : resourceOne(t, request.resourceType);
  const ids =
    request.resourceType === 'catalog.dataset' && datasetNames !== undefined
      ? [...datasetNames.keys()]
      : [];

  const draftAvailable = hasDraft || unsaved !== null;
  const usingDraft = against === 'draft' && draftAvailable;
  const stage = usingDraft
    ? draftStage(unsaved, draftRules)
    : activeStage(reading, activeRules);

  const access = {
    role: request.role,
    action: request.action,
    resourceType: request.resourceType,
    resourceId: request.resourceId === '' ? null : request.resourceId,
  };
  const result = checkAccess(access, roleGrants(roles.data?.items, access), stage);

  const set = (patch: Partial<Request>): void => {
    setChosen({ ...request, ...patch });
  };

  const target =
    request.resourceId === ''
      ? t('policy.check.anyOne', { one })
      : t('policy.rule.oneNamed', {
          one,
          name: resourceName(t, datasetNames, request.resourceId),
        });

  return (
    <div className="lq-pq__split">
      <div className="lq-pq__card lq-pq__checkForm">
        <div>
          <h3 className="lq-pq__asideTitle">{t('policy.check.title')}</h3>
          <p className="lq-pq__asideSub">{t('policy.check.sub')}</p>
        </div>
        <div className="lq-pq__field">
          <span className="lq-pq__fieldLabel" id="pq-against">
            {t('policy.check.against')}
          </span>
          <div
            className="lq-pq__seg lq-pq__seg--small"
            role="group"
            aria-labelledby="pq-against"
          >
            <button
              type="button"
              className="lq-pq__segOption"
              aria-pressed={!usingDraft}
              onClick={() => {
                setAgainst('active');
              }}
            >
              {activeSemver === null
                ? t('policy.check.activeNone')
                : t('policy.check.active', { semver: activeSemver })}
            </button>
            <button
              type="button"
              className="lq-pq__segOption"
              aria-pressed={usingDraft}
              disabled={!draftAvailable}
              onClick={() => {
                setAgainst('draft');
              }}
            >
              {unsaved !== null ? t('policy.check.unsaved') : t('policy.check.draft')}
            </button>
          </div>
        </div>
        <label className="lq-pq__field">
          <span className="lq-pq__fieldLabel">{t('policy.check.role')}</span>
          <select
            className="lq-pq__input lq-pq__select--mono"
            value={request.role}
            onChange={(event) => {
              set({ role: event.target.value });
            }}
          >
            {(vocabulary?.roles ?? []).map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </label>
        <div className="lq-pq__fieldPair">
          <label className="lq-pq__field">
            <span className="lq-pq__fieldLabel">{t('policy.check.resource')}</span>
            <select
              className="lq-pq__input"
              value={request.resourceType}
              onChange={(event) => {
                const type = event.target.value;
                const next = vocabulary?.resources.find(
                  (each) => each.resource_type === type,
                );
                set({
                  resourceType: type,
                  action: next?.actions[0] ?? '',
                  resourceId: '',
                });
              }}
            >
              {(vocabulary?.resources ?? []).map((each) => (
                <option key={each.resource_type} value={each.resource_type}>
                  {resourceLabel(t, each.resource_type)}
                </option>
              ))}
            </select>
          </label>
          <label className="lq-pq__field">
            <span className="lq-pq__fieldLabel">{t('policy.check.action')}</span>
            <select
              className="lq-pq__input"
              value={request.action}
              onChange={(event) => {
                set({ action: event.target.value });
              }}
            >
              {actions.map((action) => (
                <option key={action} value={action}>
                  {action}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="lq-pq__field">
          <span className="lq-pq__fieldLabel">{t('policy.check.which')}</span>
          <select
            className="lq-pq__input lq-pq__select--mono"
            value={request.resourceId}
            disabled={ids.length === 0}
            onChange={(event) => {
              set({ resourceId: event.target.value });
            }}
          >
            <option value="">{t('policy.check.anyOne', { one })}</option>
            {ids.map((id) => (
              <option key={id} value={id}>
                {resourceName(t, datasetNames, id)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="lq-pq__card lq-pq__verdict" role="status" aria-live="polite">
        <div className="lq-pq__verdictHead">
          <span
            className="lq-pq__verdictMark"
            data-verdict={result.verdict}
            aria-hidden="true"
          >
            {result.verdict === 'allowed' ? (
              <svg width="24" height="24" viewBox="0 0 24 24">
                <path d="m5 12.5 4.5 4.5L19 7.5" />
              </svg>
            ) : result.verdict === 'refused' ? (
              <svg width="24" height="24" viewBox="0 0 24 24">
                <path d="m7 7 10 10M17 7 7 17" />
              </svg>
            ) : (
              '?'
            )}
          </span>
          <div>
            <p className="lq-pq__verdictTitle">
              {t(`policy.check.verdict.${result.verdict}`)}
            </p>
            <p className="lq-pq__verdictLine">
              {t(
                result.verdict === 'allowed'
                  ? 'policy.check.can'
                  : result.verdict === 'refused'
                    ? 'policy.check.cannot'
                    : 'policy.check.might',
                {
                  role: request.role,
                  action: request.action,
                  target,
                },
              )}
            </p>
          </div>
        </div>
        <ol className="lq-pq__steps">
          {steps(t, result, request, datasetNames).map((step, index) => (
            <li key={step.title} className="lq-pq__step">
              <span
                className="lq-pq__stepMark"
                data-outcome={step.outcome}
                aria-hidden="true"
              >
                {MARK[step.outcome]}
              </span>
              <div>
                <p className="lq-pq__stepTitle">
                  {String(index + 1)} · {step.title}
                </p>
                <p className="lq-pq__stepText">{step.text}</p>
              </div>
            </li>
          ))}
        </ol>
        {result.namesResources ? (
          <p className="lq-pq__asideSub">{t('policy.check.pickOne')}</p>
        ) : null}
        {usingDraft && policyOff ? (
          <p className="lq-pq__asideSub">{t('policy.check.draftOff')}</p>
        ) : null}
      </div>
    </div>
  );
}

/** The draft as a policy stage: the unsaved rows when they differ, otherwise the saved draft. */
function draftStage(
  unsaved: readonly DraftRule[] | null,
  saved: readonly PolicyRule[] | null | undefined,
): PolicyStage {
  if (unsaved !== null) {
    const complete = documentFrom(unsaved).rules;
    /* Numbered by the row each rule sits on in the editor, so "rule 3" here is the third row there
       even when an incomplete row above it is left out. */
    const positions = unsaved
      .map((row, index) => ({ row, position: index + 1 }))
      .filter(({ row }) => isComplete(row))
      .map(({ position }) => position);
    const rules: NumberedRule[] = complete.map((rule, index) => ({
      rule,
      position: positions[index] ?? index + 1,
    }));
    return { kind: 'rules', rules };
  }
  if (saved === undefined || saved === null) return { kind: 'unreadable' };
  return { kind: 'rules', rules: numbered(saved) };
}

interface StepView {
  readonly title: string;
  readonly text: string;
  readonly outcome: StepOutcome;
}

function steps(
  t: TFunction,
  result: AccessCheck,
  request: Request,
  names: ReadonlyMap<string, string> | undefined,
): readonly StepView[] {
  const label = request.resourceType === '' ? '' : resourceLabel(t, request.resourceType);
  const values = {
    role: request.role,
    action: request.action,
    resources: label.toLowerCase(),
  };
  const role: StepView = {
    title: t('policy.check.stepRole'),
    outcome: result.roleGrants === null ? 'unknown' : result.roleGrants ? 'pass' : 'fail',
    text:
      result.roleGrants === null
        ? t('policy.check.roleUnknown', values)
        : result.roleGrants
          ? t('policy.check.roleHolds', values)
          : t('policy.check.roleLacks', values),
  };
  /* A role that does not hold the permission is refused before policy is consulted, so the second
     step would describe a check that never happens. */
  if (result.roleGrants === false) return [role];

  const policy = result.policy;
  let text: string;
  switch (policy.kind) {
    case 'skipped':
      text = t(`policy.check.skipped.${policy.reason}`);
      break;
    case 'refuses-all':
      text = t('policy.check.refusesAll');
      break;
    case 'unreadable':
      text = t('policy.check.unreadable');
      break;
    case 'denied-by':
      text = t('policy.check.deniedBy', {
        n: policy.match.position,
        rule: ruleSentence(t, policy.match.rule, names),
      });
      break;
    case 'allowed-by':
      text = t('policy.check.allowedBy', {
        n: policy.match.position,
        rule: ruleSentence(t, policy.match.rule, names),
      });
      break;
    case 'no-match':
      text = t('policy.check.noMatch');
      break;
  }
  return [
    role,
    { title: t('policy.check.stepPolicy'), outcome: outcomeOf(policy), text },
  ];
}
